import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section data-testid="section-newsletter" className="py-20 lg:py-28 bg-soft-pink/40">
      <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-plum tracking-tight">
            Join the club<span className="text-amber">.</span>
          </h2>
          <p className="mt-4 text-base text-plum-light leading-relaxed max-w-md mx-auto">
            New drops, comfort picks, and good vibes — straight to your inbox. No spam, just the good stuff.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          data-testid="newsletter-form"
          className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15, duration: 0.6 }}
        >
          <input
            type="email"
            data-testid="newsletter-email-input"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status !== "idle") setStatus("idle");
            }}
            placeholder="your@email.com"
            required
            className="flex-1 px-5 py-3.5 rounded-full border border-plum/15 bg-white text-plum placeholder:text-plum-muted text-sm font-sans focus:outline-none focus:ring-2 focus:ring-plum/20 transition-shadow duration-200"
          />
          <Button
            type="submit"
            data-testid="newsletter-join-btn"
            disabled={status === "loading" || status === "success"}
            className="rounded-full bg-plum text-white hover:bg-plum/90 px-8 py-3.5 font-sans font-semibold text-sm transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-70 disabled:hover:translate-y-0 flex items-center gap-2"
          >
            {status === "success" ? (
              <>
                <Check className="w-4 h-4" />
                You're in!
              </>
            ) : status === "loading" ? (
              "Joining..."
            ) : (
              <>
                Join
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </motion.form>

        {status === "error" && (
          <p data-testid="newsletter-error" className="mt-3 text-sm text-red-500 font-sans">
            Something went wrong. Please try again.
          </p>
        )}
      </div>
    </section>
  );
}
